class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // event.key before event.code: code is the physical key position and is
    // absent for synthetic events and virtual keyboards.
    this.addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape' || evt.code === 'Escape') this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

    /*
     * The close button had no handler at all — Dawn closes it with an inline
     * onclick in its own markup, and the Phase 4 rewrite of this snippet did not
     * carry that over, so the X was inert and the only ways out were the overlay
     * or Escape.
     *
     * Delegated from <cart-drawer> rather than bound to the button, because
     * renderContents() replaces #CartDrawer's innerHTML on every cart change and
     * a direct binding would be discarded with it — the same trap the overlay
     * works around by re-binding itself after each render.
     */
    this.addEventListener('click', (event) => {
      if (event.target.closest('.drawer__close')) this.close();
    });

    /*
     * Recover focus whenever the drawer's contents are replaced while it is open.
     *
     * Every cart update replaces #CartDrawer's innerHTML, destroying whatever
     * held focus — cart.js blurs it first in enableLoading() and restores it
     * afterwards. That restore targets the line that changed, so it works for a
     * quantity change but not when the last line goes: the line it wants no
     * longer exists and the empty state has replaced the markup around it, so
     * focus is left on <body>. The drawer stays open and modal over a page the
     * user has just been dropped behind, and the next Tab starts at the top of
     * the document. Verified in the browser by removing the last line.
     *
     * Watching the DOM rather than subscribing to cartUpdate: the update path
     * replaces sections, restores focus and publishes in an order that varies by
     * path, and a subscriber that fires before the final replacement just gets
     * its focus target destroyed again. The mutation is the thing that actually
     * invalidates focus, so it is the thing to react to.
     *
     * Re-arming on every mutation, not only when focus escaped, because the trap
     * itself is invalidated by the same re-render. trapFocus() closes over the
     * first and last focusable elements it found, and only attaches its Tab
     * handler when focus reaches one of them by identity — so once those nodes
     * are replaced, the references are detached, the handler is never attached
     * and Tab walks straight out of an open modal drawer into the page behind it.
     * Verified: after a quantity change, a Tab from the last element in the
     * drawer landed on the site logo.
     *
     * Passing the currently focused element back in keeps cart.js's own, more
     * precise restore — it puts focus on the line that changed — while still
     * recomputing the trap's bounds. Only when focus has genuinely escaped does
     * this fall back to the dialog. close() drops `active` before moving focus,
     * so this never pulls it back out of a closing drawer.
     */
    this.focusGuard = new MutationObserver(() => {
      if (!this.classList.contains('active')) return;

      this.trapDrawerFocus(this.contains(document.activeElement) ? document.activeElement : null);
    });
    this.focusGuard.observe(this, { childList: true, subtree: true });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');

      /*
       * Focus moves into the drawer here, in the same tick as the class — not on
       * a transitionend from this element, which is how Dawn gated it.
       *
       * That transitionend never fires in this theme. Verified in the browser on
       * both open paths (add to cart, and the header cart icon) with
       * prefers-reduced-motion off: the slide is a transform transition on
       * .drawer__inner, it runs, and no transitionend reaches <cart-drawer> —
       * not even a capturing listener. So trapFocus() was never called at all:
       * the drawer opened over the page with body scroll locked and focus left
       * on the button behind it, which is unusable from the keyboard and silent
       * to a screen reader.
       *
       * Trapping immediately is also the better behaviour — a keyboard user gets
       * the drawer now rather than 450ms later — so this does not want a
       * transition contract restored to prop it up.
       *
       * Falls back to .drawer__inner for the container because #CartDrawer and
       * the empty-state wrapper are both replaced by renderContents().
       */
      this.trapDrawerFocus();
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  /*
   * Move focus into the drawer and hold it there.
   *
   * The container is what Tab cycles within; the default focus target is
   * .drawer__inner, the role="dialog" element, so a screen reader announces the
   * drawer rather than landing on its close button. Both fall back to
   * .drawer__inner because #CartDrawer and the empty-state wrapper are replaced
   * on every re-render.
   *
   * preferredTarget keeps focus where it already is when the trap is only being
   * re-armed after a re-render, so this can recompute the trap's bounds without
   * yanking focus away from wherever cart.js just put it.
   */
  trapDrawerFocus(preferredTarget) {
    const container = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty') || this.querySelector('.drawer__inner')
      : document.getElementById('CartDrawer') || this.querySelector('.drawer__inner');
    const fallback = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    const focusElement = preferredTarget && container?.contains(preferredTarget) ? preferredTarget : fallback;

    if (container && focusElement) trapFocus(container, focusElement);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
